import { userService } from "./userService";
const API_URL = process.env.REACT_APP_API_URL;

async function authFetch(url, options = {}) {
  const headers = {
    "Content-Type": "application/json",
    ...userService._getAuthHeaders(),
    ...options.headers,
  };

  let response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    const newToken = await userService.refreshAccessToken();
    if (newToken) {
      headers["Authorization"] = `Bearer ${newToken}`;
      response = await fetch(url, { ...options, headers });
    }
  }

  return response;
}

export const documentService = {
  // ===== Upload invoices =====
  async uploadInvoicesAndIncrement(selectedFiles, userId) {
    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => formData.append("files", file));
      formData.append("userId", userId);

      const authHeaders = userService._getAuthHeaders();
      const formHeaders = { ...authHeaders };
      delete formHeaders["Content-Type"];

      const uploadResponse = await fetch(`${API_URL}/api/documents/invoices/upload`, {
        method: "POST",
        headers: formHeaders,
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error("Upload failed:", errorText);
        throw new Error("Failed to upload files");
      }

      const uploadData = await uploadResponse.json();

      const incrementResponse = await fetch(`${API_URL}/api/users/incrementAccount/${userId}`, {
        method: "POST",
        headers: authHeaders,
      });

      if (!incrementResponse.ok) throw new Error("Failed to increment account");

      const incrementData = await incrementResponse.json();

      return {
        success: true,
        autoIncrementAccount: incrementData.autoIncrementAccount,
        invoiceData: uploadData.invoice,
      };
    } catch (err) {
      console.error("❌ documentService.uploadInvoicesAndIncrement error:", err.message);
      throw err;
    }
  },

  // ===== Fetch documents for a case =====
  async getDocuments(lbaId) {
    try {
      if (!lbaId || lbaId === "N/A") {
        console.warn("No valid lbaId provided");
        return { documents: [], count: 0 };
      }

      const response = await authFetch(`${API_URL}/api/official_documents/documents/${lbaId}`, {
        method: "GET",
      });

      if (!response.ok) {
        console.warn(`Failed to fetch documents: ${response.status}`);
        return { documents: [], count: 0 };
      }

      const data = await response.json();
      const transformedDocuments = this.transformDocumentsResponse(data);

      return {
        documents: transformedDocuments,
        count: transformedDocuments.length,
        rawResponse: data,
      };
    } catch (error) {
      console.error("❌ Error fetching documents:", error);
      return { documents: [], count: 0, error: error.message };
    }
  },

  // Transform API response to frontend format
  transformDocumentsResponse(data) {
    if (!data.documents || !Array.isArray(data.documents)) return [];

    const allFiles = [];

    data.documents.forEach((doc) => {
      if (doc.files && Array.isArray(doc.files)) {
        doc.files.forEach((file, fileIndex) => {
          allFiles.push({
            documentId:       doc.documentId,
            lbaId:            doc.lbaId,
            createdAt:        doc.createdAt,
            totalFiles:       doc.totalFiles,
            id:               `${doc.documentId}-${fileIndex}`,
            name:             file.fileName,
            size:             file.fileSize,
            formattedSize:    this.formatFileSize(file.fileSize),
            kind:             this.getFileKind(file.fileName),
            date:             this.formatDate(doc.createdAt),
            icon:             this.getFileIcon(file.fileName),
            fileUrl:          `${API_URL}/api/official_documents/file/${doc.lbaId}/${fileIndex}`,
            mimeType:         doc.mimeType,
            fileIndex,
            originalDocument: doc,
          });
        });
      }
    });

    return allFiles;
  },

  // Download a single document
  async downloadDocument(doc) {
    try {
      if (!doc.lbaId || doc.fileIndex === undefined) {
        throw new Error("Missing lbaId or fileIndex for download");
      }

      const response = await authFetch(
        `${API_URL}/api/official_documents/file/${doc.lbaId}/${doc.fileIndex}`,
        { method: "GET" }
      );

      if (!response.ok) throw new Error(`Download failed with status: ${response.status}`);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = doc.name || "document";
      window.document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      window.document.body.removeChild(link);

      return blob;
    } catch (error) {
      console.error("❌ Error downloading document:", error);
      throw new Error(`Download failed: ${error.message}`);
    }
  },

  // Download all documents for a case
  async downloadAllDocuments(lbaId) {
    try {
      if (!lbaId || lbaId === "N/A") throw new Error("Valid lbaId is required");

      const response = await this.getDocuments(lbaId);
      const documents = response.documents;

      if (documents.length === 0) {
        alert("No documents available to download.");
        return;
      }

      let successCount = 0;
      let failCount = 0;

      for (const doc of documents) {
        try {
          await this.downloadDocument(doc);
          successCount++;
          await new Promise((resolve) => setTimeout(resolve, 500));
        } catch (error) {
          console.error(`Failed to download ${doc.name}:`, error);
          failCount++;
        }
      }

      alert(
        failCount === 0
          ? `Successfully downloaded all ${successCount} documents.`
          : `Download completed: ${successCount} successful, ${failCount} failed.`
      );
    } catch (error) {
      console.error("❌ Error in bulk download:", error);
      throw new Error(`Bulk download failed: ${error.message}`);
    }
  },

  // Upload a new document
  async uploadDocument(lbaId, file, metadata = {}) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("lbaId", lbaId);
      Object.keys(metadata).forEach((key) => formData.append(key, metadata[key]));

      const authHeaders = userService._getAuthHeaders();
      const formHeaders = { ...authHeaders };
      delete formHeaders["Content-Type"];

      const response = await fetch(`${API_URL}/api/official_documents/upload`, {
        method: "POST",
        headers: formHeaders,
        body: formData,
      });

      if (!response.ok) throw new Error(`Upload failed with status: ${response.status}`);

      return await response.json();
    } catch (error) {
      console.error("Error uploading document:", error);
      throw error;
    }
  },

  // Delete a document
  async deleteDocument(lbaId, fileIndex) {
    try {
      const response = await authFetch(
        `${API_URL}/api/official_documents/file/${lbaId}/${fileIndex}`,
        { method: "DELETE" }
      );

      if (!response.ok) throw new Error(`Delete failed with status: ${response.status}`);

      return true;
    } catch (error) {
      console.error("Error deleting document:", error);
      throw error;
    }
  },

  // Get total count of uploaded documents
  async getTotalUploadCount() {
    try {
      const response = await authFetch(`${API_URL}/api/official_documents/uploads/count`);

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      return data.totalFiles || 0;
    } catch (error) {
      console.error("Error fetching total upload count:", error);
      return 0;
    }
  },

  // Fetch documents by LBA ID (alternative method)
  async fetchDocumentsByLbaId(lbaId) {
    try {
      const response = await authFetch(
        `${API_URL}/api/law_firm/view-documents?lbaId=${encodeURIComponent(lbaId)}`,
        { method: "GET" }
      );

      if (!response.ok) throw new Error("Failed to fetch documents");

      return await response.json();
    } catch (error) {
      console.error("Error fetching documents:", error);
      throw error;
    }
  },

  // ===== AI DOCUMENT ANALYSIS METHODS =====

  async analyzeDocument(lbaId, fileIndex) {
    try {
      const response = await authFetch(
        `${API_URL}/api/official_documents/analyze/${lbaId}/${fileIndex}`,
        { method: "POST" }
      );

      if (!response.ok) throw new Error(`Analysis failed with status: ${response.status}`);

      return await response.json();
    } catch (error) {
      console.error("❌ Error analyzing document:", error);
      throw new Error(`Analysis failed: ${error.message}`);
    }
  },

  async analyzeAllDocuments(lbaId) {
    try {
      const response = await authFetch(
        `${API_URL}/api/official_documents/analyze-all/${lbaId}`,
        { method: "POST" }
      );

      if (!response.ok) throw new Error(`Bulk analysis failed with status: ${response.status}`);

      return await response.json();
    } catch (error) {
      console.error("❌ Error in bulk analysis:", error);
      throw new Error(`Bulk analysis failed: ${error.message}`);
    }
  },

  async getDocumentAnalysis(lbaId) {
    try {
      const response = await authFetch(
        `${API_URL}/api/official_documents/analysis/${lbaId}`,
        { method: "GET" }
      );

      if (!response.ok) {
        if (response.status === 404) {
          return {
            success: true,
            analyses: [],
            overallRisk: { overallRiskScore: 0, overallRiskLevel: "Unknown", analyzedDocuments: 0, highRiskDocuments: 0, totalDocuments: 0 },
          };
        }
        throw new Error(`Failed to get analysis: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("❌ Error getting analysis:", error);
      return {
        success: false,
        analyses: [],
        overallRisk: { overallRiskScore: 0, overallRiskLevel: "Unknown", analyzedDocuments: 0, highRiskDocuments: 0, totalDocuments: 0 },
        error: error.message,
      };
    }
  },

  async getFileAnalysis(lbaId, fileIndex) {
    try {
      const response = await authFetch(
        `${API_URL}/api/official_documents/analysis/${lbaId}/${fileIndex}`,
        { method: "GET" }
      );

      if (!response.ok) throw new Error(`Failed to get file analysis: ${response.status}`);

      return await response.json();
    } catch (error) {
      console.error("❌ Error getting file analysis:", error);
      throw error;
    }
  },

  async getDocumentsWithAnalysis(lbaId) {
    try {
      const [documentsResponse, analysisResponse] = await Promise.all([
        this.getDocuments(lbaId),
        this.getDocumentAnalysis(lbaId),
      ]);

      const documentsWithAnalysis = documentsResponse.documents.map((doc) => {
        const fileAnalysis = analysisResponse.analyses.find(
          (a) => a.fileIndex === doc.fileIndex
        );
        return {
          ...doc,
          analysis:       fileAnalysis?.analysis       || null,
          analysisStatus: fileAnalysis?.analysisStatus || "pending",
          requiresReview: fileAnalysis?.requiresReview || false,
        };
      });

      return {
        success:     true,
        documents:   documentsWithAnalysis,
        overallRisk: analysisResponse.overallRisk,
        count:       documentsWithAnalysis.length,
      };
    } catch (error) {
      console.error("❌ Error getting documents with analysis:", error);
      const documentsResponse = await this.getDocuments(lbaId);
      return {
        success:     false,
        documents:   documentsResponse.documents,
        overallRisk: { overallRiskScore: 0, overallRiskLevel: "Unknown", analyzedDocuments: 0, highRiskDocuments: 0, totalDocuments: documentsResponse.documents.length },
        count:       documentsResponse.documents.length,
        error:       error.message,
      };
    }
  },

  // ===== HELPER METHODS =====

  formatFileSize(bytes) {
    if (!bytes) return "Unknown size";
    const numBytes = parseInt(bytes);
    if (isNaN(numBytes)) return "Unknown size";
    if (numBytes < 1024) return numBytes + " bytes";
    if (numBytes < 1048576) return (numBytes / 1024).toFixed(1) + " KB";
    return (numBytes / 1048576).toFixed(1) + " MB";
  },

  getFileKind(fileName) {
    if (!fileName) return "Unknown file";
    const ext = fileName.split(".").pop().toLowerCase();
    const kindMap = {
      pdf: "PDF document", doc: "Word document", docx: "Word document",
      png: "PNG image", jpg: "JPEG image", jpeg: "JPEG image",
      gif: "GIF image", txt: "Text document", xls: "Excel spreadsheet", xlsx: "Excel spreadsheet",
    };
    return kindMap[ext] || `${ext.toUpperCase()} file`;
  },

  formatDate(dateString) {
    if (!dateString) return "Unknown date";
    try {
      const date = new Date(dateString);
      return (
        date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) +
        " at " +
        date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: true })
      );
    } catch {
      return "Invalid date";
    }
  },

  getFileIcon(fileName) {
    return fileName ? fileName.split(".").pop().toLowerCase() : "file";
  },

  canAnalyzeFile(doc) {
    const analyzableTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
    ];
    return analyzableTypes.includes(doc.mimeType) || /\.(pdf|doc|docx|txt)$/i.test(doc.name);
  },
};